import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { DASHBOARD, AUTH, PROVIDER } from '../constants/testIds';
import { Upload, FileCheck, DollarSign, LogOut, AlertCircle, CheckCircle2, Clock, BarChart3, Car, Package, MessageCircle, MapPin, Navigation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AnalyticsModule from '../components/AnalyticsModule';
import ThemeToggle from '../components/ThemeToggle';
import SubscriptionReminder from '../components/SubscriptionReminder';
import MapView from '../components/MapView';
import ChatInterface from '../components/ChatInterface';
import ProviderServices from '../components/ProviderServices';

const ProviderDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState(user?.verification_status);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [formData, setFormData] = useState({
    cedula_number: '',
    provider_type: user?.provider_type || '',
    vehicle_plate_1: '',
    vehicle_plate_2: '',
    ruc: '',
    business_name: '',
    business_phone: '',
    business_address: '',
    local_name: '',
    owner_name: ''
  });
  const [cedulaImage, setCedulaImage] = useState(null);
  const [holderImage, setHolderImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmitVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('cedula_number', formData.cedula_number);
      formDataToSend.append('provider_type', formData.provider_type);
      
      if (formData.provider_type === 'taxi' || formData.provider_type === 'delivery') {
        formDataToSend.append('vehicle_plate_1', formData.vehicle_plate_1);
        if (formData.vehicle_plate_2) {
          formDataToSend.append('vehicle_plate_2', formData.vehicle_plate_2);
        }
      }
      
      if (formData.provider_type === 'business' || formData.provider_type === 'local') {
        formDataToSend.append('ruc', formData.ruc);
        formDataToSend.append('business_name', formData.business_name);
        formDataToSend.append('business_phone', formData.business_phone);
        formDataToSend.append('business_address', formData.business_address);
      }
      
      if (formData.provider_type === 'local') {
        formDataToSend.append('local_name', formData.local_name);
        formDataToSend.append('owner_name', formData.owner_name);
      }
      
      formDataToSend.append('cedula_image', cedulaImage);
      formDataToSend.append('holder_image', holderImage);

      await axios.post(`${API}/providers/verify`, formDataToSend, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setMessage({ type: 'success', text: 'Verificación enviada exitosamente. Espera la aprobación del administrador.' });
      setVerificationStatus('pending');
      setShowVerificationForm(false);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error al enviar verificación' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const getStatusBadge = () => {
    if (verificationStatus === 'approved') {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-xl">
          <CheckCircle2 className="w-5 h-5 text-green-400" />
          <span className="text-green-400 font-medium">Verificado</span>
        </div>
      );
    } else if (verificationStatus === 'pending') {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-xl">
          <Clock className="w-5 h-5 text-yellow-400" />
          <span className="text-yellow-400 font-medium">Pendiente de Aprobación</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400 font-medium">No Verificado</span>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen relative" data-testid={DASHBOARD.providerDashboard}>
      {/* Subscription Reminder */}
      <SubscriptionReminder />
      {/* Mesh Gradient Glows */}
      <div className="mesh-glow-pink" style={{ top: '0%', right: '0%' }} />
      <div className="mesh-glow-blue" style={{ bottom: '0%', left: '0%' }} />

      {/* Header */}
      <header className="relative z-10 border-b border-white/10 bg-black/20 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
              Panel de Proveedor
            </h1>
            <p className="text-sm text-[#9CA3AF]">{user?.name}</p>
          </div>
          <div className="flex items-center gap-4">
            {getStatusBadge()}
            <ThemeToggle />
            <Button
              data-testid={AUTH.logoutButton}
              onClick={handleLogout}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8" data-testid={DASHBOARD.providerDashboard}>
        {message && (
          <div className={`mb-6 p-4 rounded-xl border flex items-start gap-3 ${
            message.type === 'success'
              ? 'bg-green-500/10 border-green-500/20'
              : 'bg-red-500/10 border-red-500/20'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            )}
            <p className={`text-sm ${message.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
              {message.text}
            </p>
          </div>
        )}

        {!user?.approved && !showVerificationForm && (
          <div className="glass-card p-8 rounded-3xl mb-8 text-center">
            <FileCheck className="w-16 h-16 text-[#FF007F] mx-auto mb-4" />
            <h2 className="text-2xl font-medium text-white mb-3" style={{ fontFamily: 'Outfit' }}>
              Completa tu Verificación
            </h2>
            <p className="text-[#9CA3AF] mb-6">
              Para comenzar a ofrecer servicios, necesitas completar el proceso de verificación.
            </p>
            <Button
              onClick={() => setShowVerificationForm(true)}
              className="gradient-button rounded-xl px-8 py-6"
            >
              Iniciar Verificación
            </Button>
          </div>
        )}

        {showVerificationForm && (
          <div className="glass-card p-8 rounded-3xl" data-testid={PROVIDER.verificationForm}>
            <h2 className="text-2xl font-medium text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Formulario de Verificación
            </h2>
            <form onSubmit={handleSubmitVerification} className="space-y-6">
              <div>
                <Label className="text-white mb-2 block">Número de Cédula *</Label>
                <Input
                  value={formData.cedula_number}
                  onChange={(e) => setFormData({ ...formData, cedula_number: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 text-white rounded-xl"
                  placeholder="1234567890"
                />
              </div>

              <div>
                <Label className="text-white mb-2 block">Tipo de Proveedor *</Label>
                <Select
                  value={formData.provider_type}
                  onValueChange={(value) => setFormData({ ...formData, provider_type: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-xl">
                    <SelectValue placeholder="Selecciona tipo" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1b1f] border-white/10 text-white">
                    <SelectItem value="taxi">Taxista</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                    <SelectItem value="business">Empresa</SelectItem>
                    <SelectItem value="local">Local/Restaurante</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(formData.provider_type === 'taxi' || formData.provider_type === 'delivery') && (
                <>
                  <div>
                    <Label className="text-white mb-2 block">Placa del Vehículo 1 *</Label>
                    <Input
                      value={formData.vehicle_plate_1}
                      onChange={(e) => setFormData({ ...formData, vehicle_plate_1: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 text-white rounded-xl"
                      placeholder="ABC-1234"
                    />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Placa del Vehículo 2 (Opcional)</Label>
                    <Input
                      value={formData.vehicle_plate_2}
                      onChange={(e) => setFormData({ ...formData, vehicle_plate_2: e.target.value })}
                      className="bg-white/5 border-white/10 text-white rounded-xl"
                      placeholder="DEF-5678"
                    />
                  </div>
                </>
              )}

              {(formData.provider_type === 'business' || formData.provider_type === 'local') && (
                <>
                  <div>
                    <Label className="text-white mb-2 block">RUC *</Label>
                    <Input
                      value={formData.ruc}
                      onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 text-white rounded-xl"
                      placeholder="1234567890001"
                    />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Nombre del Negocio *</Label>
                    <Input
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 text-white rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Teléfono del Negocio *</Label>
                    <Input
                      value={formData.business_phone}
                      onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 text-white rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Dirección del Negocio *</Label>
                    <Input
                      value={formData.business_address}
                      onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 text-white rounded-xl"
                    />
                  </div>
                </>
              )}

              {formData.provider_type === 'local' && (
                <>
                  <div>
                    <Label className="text-white mb-2 block">Nombre del Local *</Label>
                    <Input
                      value={formData.local_name}
                      onChange={(e) => setFormData({ ...formData, local_name: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 text-white rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Nombre del Dueño *</Label>
                    <Input
                      value={formData.owner_name}
                      onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 text-white rounded-xl"
                    />
                  </div>
                </>
              )}

              <div>
                <Label className="text-white mb-2 block">Foto de la Cédula *</Label>
                <div className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-[#8A2BE2] transition-colors cursor-pointer">
                  <Upload className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setCedulaImage(e.target.files[0])}
                    required
                    className="hidden"
                    id="cedula-upload"
                  />
                  <label htmlFor="cedula-upload" className="cursor-pointer">
                    <p className="text-sm text-[#9CA3AF]">
                      {cedulaImage ? cedulaImage.name : 'Haz clic para subir imagen'}
                    </p>
                  </label>
                </div>
              </div>

              <div>
                <Label className="text-white mb-2 block">Foto con la Cédula en la Mano *</Label>
                <div className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-[#8A2BE2] transition-colors cursor-pointer">
                  <Upload className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setHolderImage(e.target.files[0])}
                    required
                    className="hidden"
                    id="holder-upload"
                  />
                  <label htmlFor="holder-upload" className="cursor-pointer">
                    <p className="text-sm text-[#9CA3AF]">
                      {holderImage ? holderImage.name : 'Haz clic para subir imagen'}
                    </p>
                  </label>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  data-testid={PROVIDER.verificationSubmitButton}
                  disabled={loading}
                  className="flex-1 gradient-button rounded-xl py-6"
                >
                  {loading ? 'Enviando...' : 'Enviar Verificación'}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowVerificationForm(false)}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5 rounded-xl"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        )}

        {user?.approved && (
          <Tabs defaultValue="overview" className="space-y-6" data-testid={PROVIDER.profileCard}>
            <TabsList className="bg-white/5 border border-white/10 p-1 flex-wrap h-auto">
              <TabsTrigger value="overview" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF007F] data-[state=active]:to-[#8A2BE2] rounded-lg">
                Resumen
              </TabsTrigger>
              {(['taxi', 'delivery', 'local', 'restaurant'].includes(user?.provider_type)) && (
                <TabsTrigger value="services" data-testid="provider-tab-services" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF007F] data-[state=active]:to-[#8A2BE2] rounded-lg">
                  {user?.provider_type === 'taxi' ? <Car className="w-4 h-4 mr-1" /> : <Package className="w-4 h-4 mr-1" />}
                  {user?.provider_type === 'taxi' ? 'Viajes' : 'Pedidos'}
                </TabsTrigger>
              )}
              <TabsTrigger value="analytics" data-testid="provider-tab-analytics" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF007F] data-[state=active]:to-[#8A2BE2] rounded-lg">
                <BarChart3 className="w-4 h-4 mr-1" />
                Análisis
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              {user?.provider_type === 'taxi' ? (
                <ProviderServices />
              ) : (
              <div className="glass-card p-8 rounded-3xl">
                <h2 className="text-2xl font-medium text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                  ¡Tu cuenta está verificada!
                </h2>
                <p className="text-[#9CA3AF] mb-4">
                  Ahora puedes comenzar a ofrecer tus servicios en la plataforma.
                </p>
                <div className="mt-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                  <p className="text-sm text-[#9CA3AF] mb-2">Explora tu panel de análisis para ver:</p>
                  <ul className="text-sm text-white space-y-1 list-disc list-inside">
                    <li>Servicios prestados por día, semana, mes o año</li>
                    <li>Comisiones generadas en el tiempo</li>
                    <li>Distribución de tipos de servicios</li>
                  </ul>
                </div>
              </div>
              )}
            </TabsContent>

            <TabsContent value="analytics" data-testid="provider-analytics-content">
              <AnalyticsModule providerId={user._id} isAdmin={false} />
            </TabsContent>

            <TabsContent value="services" data-testid="provider-services-content">
              <ProviderServices />
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
};

export default ProviderDashboard;
